@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Playfair+Display:ital,wght@1,700&family=JetBrains+Mono:wght@400;500&display=swap');
@tailwind base;
@tailwind components;
@tailwind utilities;

/* ---- visual system, from the design brief ---- */
:root {
  --bg: #eef5f7;
  --surface: #e4ebed;
  --text: #0a2340;
  --muted: #4a6b78;
  --accent: #02857a;
  --accent2: #3fb3a6;
  --font-body: 'Manrope', ui-sans-serif, system-ui, sans-serif;
  --font-heading: 'Manrope', ui-sans-serif, system-ui, sans-serif;
  --font-serif: 'Playfair Display', Georgia, serif;
  --font-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
}

* { box-sizing: border-box; }

html, body { overflow-x: clip; margin: 0; }

body {
  background: var(--bg);
  color: var(--text);
  font-family: var(--font-body);
  -webkit-font-smoothing: antialiased;
  font-size: 16px;
}

.mono { font-family: var(--font-mono); }

/* italic serif accent word, everywhere */
.accent {
  font-family: var(--font-serif);
  font-style: italic;
  font-weight: 700;
  letter-spacing: 0;
  color: var(--accent);
}

/* ---------- CHAPTER: one screen tall, film plays itself ---------- */
.chapter { position: relative; height: 100svh; }
.stage {
  position: relative;
  height: 100%;
  overflow: hidden;
  background: var(--bg);
}

.stage .plate,
.stage video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.stage video { opacity: 0; transition: opacity .45s ease; }
.stage.is-live video { opacity: 1; }

.stage .veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    color-mix(in srgb, var(--bg) 58%, transparent) 0%,
    color-mix(in srgb, var(--bg) 16%, transparent) 34%,
    transparent 58%);
}
.stage .veil-deep {
  opacity: 0;
  transition: opacity .4s ease;
  background: linear-gradient(to top,
    color-mix(in srgb, var(--bg) 74%, transparent) 0%,
    color-mix(in srgb, var(--bg) 38%, transparent) 38%,
    transparent 70%);
}
.stage:has(.beat.is-on[data-depth="busy"]) .veil-deep { opacity: 1; }

.stage .veil-light {
  opacity: 0;
  transition: opacity .4s ease;
  background: color-mix(in srgb, var(--bg) 26%, transparent);
}
.stage:has(.beat.is-on[data-tone="light"]) .veil-light { opacity: 1; }

/* ---------- BEATS ---------- */
.beat {
  position: absolute;
  inset: 0;
  padding: clamp(20px, 4vw, 56px) clamp(24px, 8vw, 160px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  color: var(--text);
  pointer-events: none;
  transform: translate(calc(var(--mx, 0) * 8px),
                      calc((var(--local, .5) - .5) * -10px + var(--my, 0) * 6px + var(--exit, 0) * -10vh));
  will-change: transform;
}

/* readability scrim that follows each beat's text column, so the copy
   never sits directly on raw footage */
.beat::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity .5s ease;
  background:
    linear-gradient(to right,
      color-mix(in srgb, var(--bg) 93%, transparent) 0%,
      color-mix(in srgb, var(--bg) 88%, transparent) 24%,
      color-mix(in srgb, var(--bg) 72%, transparent) 40%,
      color-mix(in srgb, var(--bg) 40%, transparent) 54%,
      color-mix(in srgb, var(--bg) 0%, transparent) 72%);
}
.beat > * { position: relative; z-index: 1; }
.beat.is-on::before { opacity: 1; }
.beat[data-pos="right"]::before {
  background:
    linear-gradient(to left,
      color-mix(in srgb, var(--bg) 93%, transparent) 0%,
      color-mix(in srgb, var(--bg) 88%, transparent) 24%,
      color-mix(in srgb, var(--bg) 72%, transparent) 40%,
      color-mix(in srgb, var(--bg) 40%, transparent) 54%,
      color-mix(in srgb, var(--bg) 0%, transparent) 72%);
}
.beat[data-pos="center"]::before {
  background:
    radial-gradient(78% 66% at 50% 50%,
      color-mix(in srgb, var(--bg) 95%, transparent) 0%,
      color-mix(in srgb, var(--bg) 84%, transparent) 44%,
      color-mix(in srgb, var(--bg) 42%, transparent) 66%,
      color-mix(in srgb, var(--bg) 0%, transparent) 82%);
}
.beat[data-pos="bottom"]::before,
.beat[data-pos="top"]::before {
  background:
    linear-gradient(to top,
      color-mix(in srgb, var(--bg) 95%, transparent) 0%,
      color-mix(in srgb, var(--bg) 82%, transparent) 34%,
      color-mix(in srgb, var(--bg) 38%, transparent) 58%,
      color-mix(in srgb, var(--bg) 0%, transparent) 78%);
}
.beat[data-pos="top"]::before {
  background:
    linear-gradient(to bottom,
      color-mix(in srgb, var(--bg) 95%, transparent) 0%,
      color-mix(in srgb, var(--bg) 82%, transparent) 34%,
      color-mix(in srgb, var(--bg) 38%, transparent) 58%,
      color-mix(in srgb, var(--bg) 0%, transparent) 78%);
}
.beat .chips { z-index: 2; }
.beat > *:not(.chips) { opacity: var(--o, 0); will-change: transform, opacity; }
.veil + .beat > *:not(.chips) { --o: 1; }

.beat.is-on > *,
.beat.is-on .chips li { pointer-events: auto; }

.beat[data-pos="left"] { align-items: flex-start; text-align: left; padding-right: 52vw; }
.beat[data-pos="right"] { align-items: flex-start; text-align: left; padding-left: 52vw; }
.beat[data-pos="bottom"] {
  justify-content: flex-end; align-items: center; text-align: center; padding-bottom: 12vh;
}
.beat[data-pos="top"] {
  justify-content: flex-start; align-items: center; text-align: center; padding-top: 14vh;
}
.beat[data-pos="center"] {
  align-items: center; text-align: center;
  padding-left: clamp(20px, 8vw, 120px);
  padding-right: clamp(20px, 8vw, 120px);
}

.beat :is(h1, h2) {
  font-family: var(--font-heading);
  font-size: clamp(30px, 5.2vw, 68px);
  line-height: 1.02;
  letter-spacing: -.03em;
  font-weight: 700;
  margin: 0;
  max-width: 14ch;
  text-shadow: 0 1px 0 rgba(255, 255, 255, .45);
}
.beat[data-pos="bottom"] :is(h1, h2),
.beat[data-pos="top"] :is(h1, h2) { max-width: 22ch; }
.beat[data-pos="center"] :is(h1, h2) { max-width: 18ch; }
.beat :is(h1, h2) .accent { font-size: 1.04em; }

/* visible local keyword badge under the hero headline */
.beat .hero-local {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  max-width: 42ch;
  margin: 6px 0 0;
  padding: 7px 13px 7px 11px;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: clamp(11px, 1vw, 13px);
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: .04em;
  text-shadow: none;
  color: var(--text);
  background: color-mix(in srgb, var(--bg) 74%, transparent);
  border: 1px solid color-mix(in srgb, var(--text) 16%, transparent);
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
}
.beat .hero-local i {
  color: var(--accent);
  font-size: 1.1em;
  line-height: 1;
}
@media (max-width: 640px) {
  .beat .hero-local { max-width: 100%; font-size: 11px; }
}
@media (max-height: 800px) {
  #overview.beat { gap: 10px; }
  .beat .hero-local { margin-top: 0; padding: 6px 11px; }
}

.beat .sub {
  font-size: clamp(15px, 1.3vw, 18px);
  line-height: 1.5;
  max-width: 36ch;
  margin: 0;
  color: var(--text);
  font-weight: 600;
  opacity: calc(var(--o, 0) * .96);
  text-shadow: 0 1px 0 rgba(255, 255, 255, .35);
}
.beat .stamp {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0;
  font-weight: 600;
  text-shadow: 0 1px 0 rgba(255, 255, 255, .4);
}
/* brand lockup at the top of the opening act */
.beat .hero-logo {
  width: clamp(178px, 17.5vw, 246px);
  height: auto;
  aspect-ratio: 1 / 1;
  flex: none;
  object-fit: contain;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid color-mix(in srgb, var(--text) 8%, transparent);
  margin: -4px 0 2px;
}
@media (max-height: 820px) {
  .beat .hero-logo { width: clamp(148px, 15.2vw, 188px); }
}

.beat .points {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
  font-size: clamp(14px, 1.2vw, 17px);
}
.beat .points li::before { content: "— "; opacity: .55; }

.cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 13px 22px;
  border-radius: 999px;
  background: var(--accent);
  color: #f4fbfa;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  border: 0;
  white-space: nowrap;
  cursor: pointer;
  transition: background .25s ease, transform .25s ease;
}
.cta:hover { background: #016c63; }
.cta:hover i { transform: translateX(4px); }
.cta i { transition: transform .25s ease; }
.cta.ghost {
  background: transparent;
  color: var(--text);
  border: 1px solid color-mix(in srgb, var(--text) 24%, transparent);
}
.cta.ghost:hover { background: color-mix(in srgb, var(--surface) 80%, transparent); border-color: var(--accent); }

/* headline line reveal */
.beat .line {
  display: block;
  overflow: hidden;
  padding: .12em .08em .22em;
  margin: -.12em -.08em -.22em;
}
.beat .line-in { display: block; transform: translateY(calc(var(--o-inv, 1) * 130%)); }

/* motion presets */
.beat[data-motion="rise"] > *:not(.chips) {
  transform: translateY(calc(var(--o-inv, 1) * var(--dir, -1) * -36px));
}
.beat[data-motion="drift"] > *:not(.chips) {
  transform: translateX(calc(var(--o-inv, 1) * var(--dir, -1) * var(--side, -1) * 48px));
}
.beat[data-pos="right"][data-motion="drift"] { --side: 1; }
.beat[data-motion="zoom"] > *:not(.chips) {
  transform: scale(calc(1 + var(--o-inv, 1) * var(--dir, -1) * -.06));
  transform-origin: left center;
}
.beat[data-motion="track"] :is(h1, h2) {
  letter-spacing: calc(-.03em + var(--o-inv, 1) * .12em);
}
.beat[data-motion="track"] > *:not(.chips) {
  transform: translateY(calc(var(--o-inv, 1) * var(--dir, -1) * -16px));
}
.beat.is-leaving > *:not(.chips) { filter: blur(calc(var(--o-inv, 0) * 6px)); }

/* chips */
.beat .chips {
  list-style: none;
  margin: 0;
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translate(calc(var(--mx, 0) * 10px),
                       calc(-50% + (var(--local, .5) - .5) * -34px + var(--my, 0) * 8px + var(--exit, 0) * -8vh));
  display: grid;
  gap: 10px;
}
.beat .chips[data-side="right"] { right: clamp(24px, 8vw, 160px); justify-items: end; }
.beat .chips[data-side="left"] { left: clamp(24px, 8vw, 160px); justify-items: start; }
.beat .chips li {
  padding: 9px 14px;
  border-radius: 999px;
  font-size: 13px;
  color: var(--text);
  background: color-mix(in srgb, #f6fbfc 76%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent) 22%, transparent);
  backdrop-filter: blur(8px);
  opacity: var(--o, 0);
  transform: translateY(calc(var(--o-inv, 1) * 12px));
  white-space: nowrap;
}

/* scroll cue */
.stage .scroll-cue {
  position: absolute;
  left: 50%;
  bottom: calc(52px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  width: 22px;
  height: 34px;
  border-radius: 12px;
  border: 1.5px solid color-mix(in srgb, var(--text) 35%, transparent);
  opacity: calc(var(--cue, 1) * (1 - var(--exit, 0)) * .75);
  transition: opacity .5s ease;
  z-index: 3;
  pointer-events: none;
}
.stage .scroll-cue span {
  position: absolute;
  left: 50%;
  top: 7px;
  width: 3px;
  height: 6px;
  border-radius: 2px;
  background: var(--text);
  margin-left: -1.5px;
  animation: cue-drop 1.9s ease-in-out infinite;
}
@keyframes cue-drop {
  0%, 100% { transform: translateY(0); opacity: 1; }
  55% { transform: translateY(11px); opacity: .25; }
}

/* static (reduced motion) fallback */
.chapter.is-static { height: auto; }
.chapter.is-static .stage {
  position: relative; height: auto; overflow: visible; background: transparent;
}
.chapter.is-static .plate { position: fixed; z-index: -1; }
.chapter.is-static .veil { position: fixed; z-index: -1; }
.chapter.is-static .beat {
  position: relative; inset: auto; min-height: 100svh; pointer-events: auto;
  transform: none;
  padding-left: clamp(20px, 4vw, 56px);
  padding-right: clamp(20px, 4vw, 56px);
}
.chapter.is-static .beat > *:not(.chips),
.chapter.is-static .beat .chips li,
.chapter.is-static .line-in { --o: 1; --o-inv: 0; transform: none !important; filter: none !important; }
.chapter.is-static .beat .chips { position: static; transform: none; display: flex; flex-wrap: wrap; }
.chapter.is-static video { display: none; }
.chapter.is-static .scroll-cue { display: none; }

/* ---------- fixed nav ---------- */
.site-nav {
  --nav-ink: var(--text);
  --nav-muted: var(--muted);
  --nav-line: color-mix(in srgb, var(--text) 22%, transparent);
  --nav-shadow: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px clamp(20px, 4vw, 56px);
  transition: background .35s ease, border-color .35s ease, padding .35s ease;
  border-bottom: 1px solid transparent;
}

/* over the film: cream ink + soft top scrim so the menu never gets lost */
.site-nav:not(.is-solid) {
  --nav-ink: #f7fdfe;
  --nav-muted: rgba(238, 249, 250, .82);
  --nav-line: rgba(247, 253, 254, .5);
  --nav-shadow: 0 1px 2px rgba(4, 20, 32, .5), 0 2px 14px rgba(4, 20, 32, .35);
}
.site-nav:not(.is-solid)::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background: linear-gradient(to bottom,
    rgba(6, 30, 44, .5) 0%,
    rgba(6, 30, 44, .26) 58%,
    rgba(6, 30, 44, 0) 100%);
}
.site-nav.is-solid::after { display: none; }

.nav-ink { color: var(--nav-ink); text-shadow: var(--nav-shadow); transition: color .35s ease; }
.nav-muted { color: var(--nav-muted); text-shadow: var(--nav-shadow); transition: color .35s ease; }

.nav-logo {
  width: 48px;
  height: 48px;
  flex: none;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(4, 20, 32, .28);
}
.nav-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform: none;
}
.site-nav.is-solid .nav-logo { box-shadow: none; border: 1px solid color-mix(in srgb, var(--text) 10%, transparent); }
.site-nav.is-solid {
  background: color-mix(in srgb, var(--bg) 88%, transparent);
  backdrop-filter: blur(14px);
  border-bottom-color: color-mix(in srgb, var(--text) 10%, transparent);
  padding-top: 12px;
  padding-bottom: 12px;
}
.nav-link {
  position: relative;
  font-size: 14.5px;
  font-weight: 600;
  letter-spacing: -.01em;
  color: var(--nav-ink);
  text-shadow: var(--nav-shadow);
  text-decoration: none;
  padding: 4px 0;
  white-space: nowrap;
  transition: color .25s ease;
}
.nav-link-accent { color: var(--accent); }
.site-nav:not(.is-solid) .nav-link-accent { color: #a8efe6; }
.nav-cta { box-shadow: 0 2px 12px rgba(4, 20, 32, .28); }
.site-nav.is-solid .nav-cta { box-shadow: none; }

/* phones: keep the WhatsApp action as a compact round icon only */
.site-nav .nav-cta {
  width: 40px;
  height: 40px;
  padding: 0;
  justify-content: center;
  gap: 0;
}
@media (min-width: 768px) {
  .site-nav .nav-cta {
    width: auto;
    height: auto;
    padding: 13px 22px;
    gap: 10px;
  }
}
@media (max-width: 767px) {
  .site-nav {
    gap: 8px;
    padding-left: 14px;
    padding-right: 14px;
  }
  .site-nav .nav-brand { gap: 7px; min-width: 0; }
  .site-nav .nav-brand > span:last-child { min-width: 0; overflow: hidden; }
  .site-nav .nav-logo { width: 36px; height: 36px; border-radius: 10px; }
  .site-nav .nav-brand .nav-ink { font-size: 13px; letter-spacing: -.025em; }
  .site-nav .nav-tagline {
    font-size: 6.8px;
    letter-spacing: .07em;
    margin-top: 2px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .site-nav .lang-switch {
    height: 40px;
    padding: 3px;
    gap: 1px;
  }
  .site-nav .lang-switch-btn {
    font-size: 9px;
    letter-spacing: .04em;
    min-width: 22px;
    padding: 0 6px;
  }
}
@media (max-width: 359px) {
  .site-nav .nav-tagline { display: none; }
}
.nav-burger {
  border: 1px solid var(--nav-line);
  color: var(--nav-ink);
  text-shadow: var(--nav-shadow);
  transition: border-color .25s ease, color .25s ease;
}
.nav-link::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  height: 1.5px;
  width: 100%;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform .3s ease;
}
.nav-link:hover::after { transform: scaleX(1); }

/* one slim segmented pill instead of two wide labelled pills */
.lang-switch {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: none;
  height: 44px;
  padding: 4px;
  border-radius: 999px;
  border: 1px solid var(--nav-line);
  background: transparent;
  transition: border-color .25s ease;
}
.lang-switch-btn {
  font-family: var(--font-mono);
  font-size: 10px;
  line-height: 1;
  letter-spacing: .06em;
  height: 100%;
  min-width: 25px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  background: transparent;
  color: var(--nav-muted);
  text-shadow: var(--nav-shadow);
  transition: background .25s ease, color .25s ease;
}
.lang-switch-btn[data-active="true"] {
  background: var(--nav-ink);
  color: #f7fdfe;
  text-shadow: none;
}
.site-nav:not(.is-solid) .lang-switch-btn[data-active="true"] { color: #0a2340; }

/* ---------- PANELS ---------- */
.panel {
  position: relative;
  min-height: 100svh;
  overflow: clip;
  display: flex;
  align-items: center;
  padding: clamp(20px, 4vw, 56px) clamp(24px, 8vw, 160px);
  color: var(--text);
}
.panel-bg {
  position: absolute;
  inset: -35% 0;
  width: 100%;
  height: 170%;
  object-fit: cover;
  z-index: 0;
  filter: blur(var(--soften, 0px));
}
.panel-veil {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(to right,
    color-mix(in srgb, var(--bg) 86%, transparent) 0%,
    color-mix(in srgb, var(--bg) 52%, transparent) 42%,
    transparent 72%);
}
.panel[data-side="right"] .panel-veil {
  background: linear-gradient(to left,
    color-mix(in srgb, var(--bg) 86%, transparent) 0%,
    color-mix(in srgb, var(--bg) 52%, transparent) 42%,
    transparent 72%);
}
.panel[data-side="bottom"] .panel-veil {
  background: linear-gradient(to top,
    color-mix(in srgb, var(--bg) 88%, transparent) 0%,
    color-mix(in srgb, var(--bg) 46%, transparent) 45%,
    transparent 76%);
}
.panel-copy {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 46ch;
  transform: translate(calc(var(--mx, 0) * 14px), calc(var(--py, 0px) + var(--my, 0) * 10px));
  will-change: transform;
}
.panel[data-side="right"] { justify-content: flex-end; }
.panel[data-side="bottom"] {
  align-items: flex-end;
  justify-content: center;
  text-align: center;
  padding-bottom: 12vh;
}
.panel-accent {
  position: absolute;
  z-index: 1;
  transform: translate(calc(var(--mx, 0) * 24px), calc(var(--py, 0px) + var(--my, 0) * 16px));
  pointer-events: none;
  will-change: transform;
}
.panel-copy h2 {
  font-family: var(--font-heading);
  font-size: clamp(30px, 5.2vw, 66px);
  line-height: 1.03;
  letter-spacing: -.03em;
  font-weight: 600;
  margin: 0;
  padding-bottom: .12em;
}
.panel-copy .sub {
  font-size: clamp(15px, 1.25vw, 18px);
  line-height: 1.6;
  color: var(--text);
  margin: 0;
  max-width: 44ch;
}
.panel .stamp {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0;
  font-weight: 500;
}

/* numbered card list */
.num-list {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
  max-width: 520px;
  width: 100%;
}
.num-list li {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--bg) 78%, transparent);
  border: 1px solid color-mix(in srgb, var(--text) 12%, transparent);
  backdrop-filter: blur(10px);
  transition: transform .25s ease-out, border-color .25s ease-out;
}
.num-list li:hover {
  transform: translateY(-4px);
  border-color: color-mix(in srgb, var(--accent) 45%, transparent);
}
.num-list .idx {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--accent);
  padding-top: 2px;
  font-weight: 500;
}
.num-list .t {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 3px;
}
.num-list .d {
  font-size: 14px;
  line-height: 1.45;
  color: var(--muted);
  margin: 0;
}

/* manifesto */
.manifesto {
  justify-content: center;
  align-items: center;
  text-align: center;
  background: var(--bg);
}
.manifesto-text {
  position: relative;
  z-index: 2;
  max-width: 22ch;
  margin: 0;
  font-family: var(--font-heading);
  font-size: clamp(30px, 5.2vw, 68px);
  line-height: 1.08;
  letter-spacing: -.03em;
  font-weight: 600;
}
.manifesto .w {
  color: color-mix(in srgb, var(--text) calc(var(--lit, 0) * 100%), var(--muted));
  transition: color .18s linear;
}
.manifesto .accent .w {
  color: color-mix(in srgb, var(--accent) calc(var(--lit, 0) * 100%), var(--muted));
}

/* numbers band */
.band {
  position: relative;
  background: var(--surface);
  border-top: 1px solid color-mix(in srgb, var(--text) 10%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--text) 10%, transparent);
  padding: 72px clamp(20px, 6vw, 120px);
}
.band-grid {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 40px 32px;
}
@media (min-width: 900px) {
  .band-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
.band-figure {
  font-family: var(--font-serif);
  font-style: italic;
  font-weight: 700;
  font-size: clamp(48px, 5.4vw, 76px);
  line-height: 1;
  letter-spacing: -.03em;
  color: var(--text);
}
.band-figure .unit {
  font-size: .5em;
  vertical-align: super;
  color: var(--accent);
  letter-spacing: 0;
}
.band-cap {
  font-size: 14px;
  color: var(--muted);
  max-width: 20ch;
  margin-top: 12px;
  line-height: 1.5;
}

/* FAQ */
.faq-section {
  position: relative;
  background: var(--bg);
  padding: clamp(72px, 10vw, 140px) clamp(20px, 8vw, 160px);
  overflow: clip;
}
.faq-inner { max-width: 1200px; margin: 0 auto; display: grid; gap: 40px; }
@media (min-width: 900px) {
  .faq-inner { grid-template-columns: minmax(0, 4fr) minmax(0, 6fr); gap: 64px; }
}
.faq-row {
  border-top: 1px solid color-mix(in srgb, var(--text) 14%, transparent);
}
.faq-row:last-child { border-bottom: 1px solid color-mix(in srgb, var(--text) 14%, transparent); }
.faq-q {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 0;
  background: none;
  border: 0;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
  transition: color .2s ease;
}
.faq-q:hover { color: var(--accent); }
.faq-q i { transition: transform .3s ease; flex: none; }
.faq-row[data-open="true"] .faq-q i { transform: rotate(45deg); }
.faq-a {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows .35s ease;
}
.faq-row[data-open="true"] .faq-a { grid-template-rows: 1fr; }
.faq-a > div { overflow: hidden; }
.faq-a p {
  margin: 0 0 22px;
  font-size: 15px;
  line-height: 1.65;
  color: var(--muted);
  max-width: 52ch;
}

/* contact form card */
.form-card {
  margin-top: 10px;
  background: color-mix(in srgb, var(--bg) 90%, transparent);
  border: 1px solid color-mix(in srgb, var(--text) 12%, transparent);
  border-radius: 14px;
  padding: 22px;
  backdrop-filter: blur(14px);
  display: grid;
  gap: 14px;
  max-width: 520px;
  width: 100%;
  transform: translate(calc(var(--mx, 0) * 8px), calc(var(--my, 0) * 6px));
}
.field { display: grid; gap: 6px; min-width: 0; }
.field label {
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--muted);
}
.field input,
.field select,
.field textarea {
  width: 100%;
  box-sizing: border-box;
  background: #fbfdfd;
  border: 1px solid color-mix(in srgb, var(--text) 14%, transparent);
  border-radius: 8px;
  padding: 11px 13px;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--text);
  outline: none;
  transition: border-color .2s ease, box-shadow .2s ease;
  appearance: none;
}
.field textarea { resize: vertical; min-height: 96px; line-height: 1.5; }
.field input::placeholder,
.field textarea::placeholder { color: color-mix(in srgb, var(--muted) 75%, transparent); font-size: 14px; }
.field input:focus,
.field select:focus,
.field textarea:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 20%, transparent);
}
.field input[type="hidden"] { display: none; }
.lang-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--accent);
  font-weight: 500;
}
.lang-note i { font-size: 15px; line-height: 1; }
.lang-pick { display: inline-flex; gap: 6px; flex-wrap: wrap; }
.lang-pick button {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .1em;
  text-transform: uppercase;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  background: transparent;
  border: 1px solid color-mix(in srgb, var(--text) 22%, transparent);
  color: var(--muted);
  transition: background .25s ease, border-color .25s ease, color .25s ease;
}
.lang-pick button:hover { border-color: color-mix(in srgb, var(--accent) 55%, transparent); color: var(--accent); }
.lang-pick button[data-active="true"] {
  background: var(--accent);
  border-color: var(--accent);
  color: #f4fbfa;
}
.wa-block {
  display: grid;
  gap: 10px;
  margin-top: 4px;
  padding: 16px 18px;
  width: 100%;
  max-width: 520px;
  border-radius: 14px;
  background: color-mix(in srgb, var(--bg) 84%, transparent);
  border: 1px solid color-mix(in srgb, var(--text) 12%, transparent);
  backdrop-filter: blur(12px);
  transform: translate(calc(var(--mx, 0) * 8px), calc(var(--my, 0) * 6px));
}
.wa-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--muted);
}
.wa-head i { font-size: 16px; line-height: 1; color: var(--accent); }
.wa-head a {
  color: var(--accent);
  text-decoration: none;
  letter-spacing: .06em;
  white-space: nowrap;
  transition: color .2s ease;
}
.wa-head a:hover { color: #016c63; }
.wa-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.wa-row .cta { padding: 10px 16px; font-size: 14px; }
.wa-note {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--muted);
}
.field-row { display: grid; gap: 14px; }
@media (min-width: 640px) { .field-row { grid-template-columns: 1fr 1fr; } }
.form-status { font-size: 14px; line-height: 1.5; margin: 0; }
.form-status[data-kind="ok"] { color: var(--accent); }
.form-status[data-kind="err"] { color: #a33a2c; }

/* automatic WhatsApp rescue when the form endpoint fails */
.form-fallback {
  display: grid;
  gap: 8px;
  padding: 14px 15px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--accent) 9%, #fbfdfd);
  border: 1px dashed color-mix(in srgb, var(--accent) 46%, transparent);
  animation: fallback-in .38s cubic-bezier(.2, .7, .2, 1) both;
}
@keyframes fallback-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
}
.form-fallback-title {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -.01em;
  line-height: 1.35;
  color: var(--text);
}
.form-fallback-title i { font-size: 16px; line-height: 1; flex: none; color: var(--accent); }
.form-fallback-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--muted);
}
.form-fallback-btn {
  justify-self: start;
  margin-top: 2px;
  padding: 10px 16px;
  font-size: 14px;
}
.form-fallback-btn i { font-size: 15px; }
.hp-field {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

/* keep the assistant pill inside the viewport on small screens */
vapi-widget { max-width: calc(100vw - 24px); }

/* soft nudge above the floating chat button */
.chat-hint {
  position: fixed;
  right: 24px;
  bottom: 108px;
  z-index: 62;
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: min(330px, calc(100vw - 32px));
  padding: 8px 8px 8px 14px;
  border-radius: 999px;
  background: #fbfdfd;
  border: 1px solid color-mix(in srgb, var(--text) 13%, transparent);
  backdrop-filter: blur(10px);
  opacity: 0;
  transform: translateY(10px) scale(.98);
  pointer-events: none;
  transition: opacity .45s ease, transform .45s ease;
}
.chat-hint.is-in {
  opacity: 1;
  transform: none;
  pointer-events: auto;
}
.chat-hint-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
  text-align: left;
  color: var(--text);
  font-family: var(--font-body);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.35;
}
.chat-hint-main i { color: var(--accent); font-size: 15px; line-height: 1; flex: none; }
.chat-hint-main:hover span { color: var(--accent); }
.chat-hint-main span { transition: color .2s ease; }
.chat-hint-x {
  flex: none;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--muted);
  font-size: 13px;
  cursor: pointer;
  transition: background .2s ease, color .2s ease;
}
.chat-hint-x:hover { background: color-mix(in srgb, var(--text) 8%, transparent); color: var(--text); }

@media (max-width: 640px) {
  .chat-hint { right: 16px; bottom: 100px; padding: 7px 7px 7px 12px; }
  .chat-hint-main { font-size: 12px; }
}

/* reveal + parallax */
[data-reveal] {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity .7s cubic-bezier(.2,.7,.2,1), transform .7s cubic-bezier(.2,.7,.2,1);
  transition-delay: calc(var(--i, 0) * 80ms);
}
[data-reveal].is-in { opacity: 1; transform: none; }
[data-parallax]:not(.panel-copy):not(.panel-accent) {
  transform: translateY(var(--py, 0px));
  will-change: transform;
}
.card { transition: transform .25s ease-out, border-color .25s ease-out; }
.card:hover { transform: translateY(-4px); border-color: color-mix(in srgb, var(--accent) 40%, transparent); }

/* reviews */
.reviews-section {
  position: relative;
  background: color-mix(in srgb, var(--surface) 58%, var(--bg));
  border-top: 1px solid color-mix(in srgb, var(--text) 8%, transparent);
  padding: clamp(72px, 9vw, 128px) clamp(20px, 8vw, 160px);
  overflow: clip;
}
.reviews-inner { max-width: 1400px; margin: 0 auto; }
.reviews-head { display: grid; gap: 14px; max-width: 60ch; }
.reviews-head .stamp {
  font-size: 11px;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0;
  font-weight: 600;
}
.reviews-head h2 {
  font-family: var(--font-heading);
  font-size: clamp(30px, 4.6vw, 58px);
  line-height: 1.03;
  letter-spacing: -.03em;
  font-weight: 600;
  margin: 0;
  padding-bottom: .12em;
}
.reviews-sub {
  font-size: clamp(15px, 1.15vw, 17px);
  line-height: 1.6;
  color: var(--muted);
  margin: 0;
  max-width: 54ch;
}
.reviews-grid {
  margin-top: clamp(32px, 4vw, 56px);
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
}
@media (min-width: 700px) {
  .reviews-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 1120px) {
  .reviews-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
.review-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 22px;
  min-width: 0;
  border-radius: 14px;
  background: #fbfdfd;
  border: 1px solid color-mix(in srgb, var(--text) 11%, transparent);
  transition: transform .28s ease-out, border-color .28s ease-out;
}
.review-card:hover {
  transform: translateY(-4px);
  border-color: color-mix(in srgb, var(--accent) 42%, transparent);
}
.review-stars { color: var(--accent); }
.review-text {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: color-mix(in srgb, var(--text) 88%, var(--muted));
  flex: 1 1 auto;
}
.review-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
  padding-top: 10px;
  border-top: 1px solid color-mix(in srgb, var(--text) 9%, transparent);
}
.review-name { font-size: 13.5px; font-weight: 700; letter-spacing: -.01em; }
.review-meta { font-size: 10.5px; letter-spacing: .06em; color: var(--muted); }

.review-score {
  background: #082033;
  border-color: #082033;
  color: #e8f1f3;
  justify-content: center;
  gap: 6px;
}
.review-score:hover { transform: none; border-color: #082033; }
.score-figure {
  font-family: var(--font-serif);
  font-style: italic;
  font-weight: 700;
  font-size: clamp(52px, 5vw, 66px);
  line-height: 1;
  letter-spacing: -.04em;
}
.score-stars { color: var(--accent2); }
.score-cap {
  margin: 10px 0 0;
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--accent2);
}
.score-count { margin: 0 0 auto; font-size: 13.5px; color: #a9c1c9; }
.score-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  margin-top: 12px;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.1);
  color: #e8f1f3;
}

/* footer */
.site-footer {
  background: #082033;
  color: #e8f1f3;
  padding: clamp(56px, 7vw, 96px) clamp(20px, 8vw, 120px) 32px;
}
.site-footer a { color: #cfe0e4; text-decoration: none; transition: color .2s ease; }
.site-footer a:hover { color: var(--accent2); }

.stage video, .plate, .panel-bg { filter: blur(var(--soften, 0px)); }

@media (max-width: 780px) {
  .beat[data-pos="left"], .beat[data-pos="right"],
  .beat[data-pos="top"], .beat[data-pos="bottom"] {
    justify-content: flex-end;
    align-items: flex-start;
    text-align: left;
    padding: 0 20px calc(12vh + env(safe-area-inset-bottom, 0px)) 20px;
  }
  .beat :is(h1, h2),
  .beat[data-pos="bottom"] :is(h1, h2),
  .beat[data-pos="top"] :is(h1, h2) { font-size: clamp(28px, 8.2vw, 42px); max-width: 15ch; }
  .beat .sub { font-size: 15px; max-width: 34ch; }
  .beat .points { font-size: 14px; }
  .beat .hero-logo { width: 128px; border-radius: 14px; }
  .beat { transform: translateY(calc((var(--local, .5) - .5) * -10px + var(--exit, 0) * -8vh)); }
  .stage .scroll-cue { display: none; }
  .beat .chips {
    position: static;
    transform: none !important;
    order: -1;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 2px;
  }
  .beat .chips li { font-size: 12px; padding: 6px 10px; }
  .beat[data-motion="drift"] > *:not(.chips),
  .beat[data-motion="zoom"] > *:not(.chips) {
    transform: translateY(calc(var(--o-inv, 1) * var(--dir, -1) * -28px));
  }
  .stage .veil {
    background: linear-gradient(to top, rgba(238,245,247,.88) 0%, rgba(238,245,247,.55) 40%, transparent 74%);
  }
  .beat::before,
  .beat[data-pos="right"]::before,
  .beat[data-pos="center"]::before,
  .beat[data-pos="bottom"]::before,
  .beat[data-pos="top"]::before {
    background: linear-gradient(to top,
      color-mix(in srgb, var(--bg) 94%, transparent) 0%,
      color-mix(in srgb, var(--bg) 86%, transparent) 30%,
      color-mix(in srgb, var(--bg) 52%, transparent) 52%,
      color-mix(in srgb, var(--bg) 0%, transparent) 76%);
  }
  .chapter.is-static .beat::before { opacity: .9; }

  .panel {
    align-items: flex-end;
    padding: 0 20px calc(10vh + env(safe-area-inset-bottom, 0px)) 20px;
  }
  .panel-copy { max-width: none; text-align: left; }
  .panel[data-side="bottom"] { text-align: left; }
  .panel-veil,
  .panel[data-side="right"] .panel-veil {
    background: linear-gradient(to top,
      rgba(4, 20, 32, .82) 0%,
      rgba(4, 20, 32, .58) 46%,
      rgba(4, 20, 32, .24) 74%,
      rgba(4, 20, 32, 0) 100%);
  }
  /* copy reads light-on-photo once the image stays visible */
  .panel .stamp {
    color: #8ff0e4;
    text-shadow: 0 1px 3px rgba(4, 20, 32, .85), 0 2px 12px rgba(4, 20, 32, .6);
  }
  .panel-copy h2 {
    color: #ffffff;
    text-shadow: 0 2px 5px rgba(4, 20, 32, .65), 0 6px 26px rgba(4, 20, 32, .5);
  }
  .panel-copy h2 .accent {
    color: #9df3e8;
    text-shadow: 0 2px 5px rgba(4, 20, 32, .55), 0 6px 22px rgba(4, 20, 32, .45);
  }
  .panel-copy .sub {
    color: #f1f8fa;
    text-shadow: 0 1px 4px rgba(4, 20, 32, .8), 0 2px 14px rgba(4, 20, 32, .55);
  }
  /* the language line is redundant on phones — the form states it explicitly */
  .panel .lang-note { display: none; }
  .manifesto-text { font-size: clamp(26px, 7.5vw, 40px); max-width: none; }
}

@media (prefers-reduced-motion: reduce) {
  .stage video { display: none; }
  .stage .scroll-cue span { animation: none; }
  [data-reveal] { opacity: 1; transform: none; transition: none; }
  [data-parallax] { transform: none !important; }
}